/*
 * Copyright (c) 2022 Puratos. All rights reserved.
 */

import * as React from 'react';
import { FunctionComponent } from 'react';
import { useI18N } from '../../hoc/withI18N';
import { useSelector } from 'react-redux';
import { selectIsPricingVatIncluded } from '../../../selectors/my-puratos/account';
import { ShowHide } from '../../atoms/layout/ShowHide';
import { getTotalCartItemQuantity } from '../../../selectors/cart';
import ProductPackageSelector from '../../molecules/products/ProductPackageSelector';
import {
    getActiveSku,
    getActiveSkuFromVariations,
    Store,
} from '../../../selectors/productPage';
import { RequestPriceButton } from '../../molecules/products/RequestPriceButton';
import { IECOMVariationPricing } from '../../../interfaces/organisms/product-page/IECOMVariatonPricing';
import {
    IPackaging,
    IVariation,
} from '../../../interfaces/my-puratos/IGeneric';

export const ECOMVariationPricing: FunctionComponent<IECOMVariationPricing> = ({
    variations,
    obsolete,
    homepage,
    productCategoryTag
}) => {
    const translate = useI18N();
    const vatIncluded: boolean = useSelector(selectIsPricingVatIncluded);
    const sku: IPackaging = useSelector(getActiveSku) || undefined;
    const variation: IVariation =
        useSelector((state: Store) =>
            getActiveSkuFromVariations(state, variations)
        ) || undefined;
    const itemsInCart: number = useSelector(
        (state) =>
            getTotalCartItemQuantity(state, variation?.materialNumber) || 0
    );

    return (
        <React.Fragment>
            <div className="product-page__pricing--details">
                <ProductPackageSelector
                    variations={variations}
                    obsolete={obsolete}
                />

                {sku && (
                    <ShowHide show={sku.hasPrice && !sku.isObsolete}>
                        <div className="product-page__pricing--details-price">
                            <span>{translate('Unit price')}</span>
                            <div className="price">
                                <div className="unit-price">
                                    <span
                                        className={
                                            'price-standard' +
                                            (sku.promotionUnitPriceDouble > 0
                                                ? ' price-lt'
                                                : '')
                                        }
                                    >
                                        {sku.unitPrice}
                                    </span>

                                    {sku.promotionUnitPriceDouble > 0 && (
                                        <span className="price-promo">
                                            {sku.promotionUnitPrice}
                                        </span>
                                    )}
                                </div>
                                <span>
                                    {vatIncluded
                                        ? translate('incl. vat')
                                        : translate('excl. vat')}
                                </span>
                            </div>
                        </div>
                        <div className="product-page__pricing--details-price">
                            <span>{translate('Base price')}</span>
                            <div className="price">
                                <div className="base-price">
                                    <span
                                        className={
                                            'price-standard' +
                                            (sku.promotionWeightPriceDouble > 0
                                                ? ' price-lt'
                                                : '')
                                        }
                                    >
                                        {sku.weightPrice}
                                        {sku.promotionWeightPriceDouble > 0
                                            ? null
                                            : '/' + translate(sku.baseUnit)}
                                    </span>
                                    {sku.promotionWeightPriceDouble > 0 && (
                                        <span className="price-promo">
                                            {sku.promotionWeightPrice}/
                                            {translate(sku.baseUnit)}
                                        </span>
                                    )}
                                </div>
                                <span>
                                    {vatIncluded
                                        ? translate('incl. vat')
                                        : translate('excl. vat')}
                                </span>
                            </div>
                        </div>
                    </ShowHide>
                )}

                {sku && (
                    <ShowHide show={!sku.isObsolete && !sku.hasPrice}>
                        <div className="product-page__pricing--details-message">
                            <span className="important-hide-mobile">
                                &nbsp;
                            </span>
                            <div
                                className={
                                    'product-page__pricing--no-price text-muted'
                                }
                            >
                                <p
                                    dangerouslySetInnerHTML={{
                                        __html: homepage.accompanyingButtonText,
                                    }}
                                />
                                <RequestPriceButton
                                    productId={sku.materialNumber}
                                    productCategoryTag={productCategoryTag}
                                />
                            </div>
                        </div>
                    </ShowHide>
                )}

                <ShowHide
                    show={!variation?.orderable || !sku || sku.isObsolete}
                >
                    <div className="product-page__pricing--details-message">
                        <span className="important-hide-mobile">&nbsp;</span>
                        <div className="product-page__pricing--no-price">
                            <p className={'text-muted p-margin-top-sm'}>
                                {homepage.obsoleteProduct}
                            </p>
                        </div>
                    </div>
                </ShowHide>
            </div>

            {itemsInCart > 0 ? (
                <div
                    className={
                        'product-page__pricing--cart-amount p-padding-left p-padding-right p-margin-bottom-sm'
                    }
                >
                    <div
                        className={
                            'product-page__alert product-page__alert--warning p-margin-none'
                        }
                    >
                        {translate(
                            itemsInCart > 1
                                ? 'You currently have {0} items in your cart'
                                : 'You currently have {0} item in your cart'
                        ).replace('{0}', itemsInCart)}
                    </div>
                </div>
            ) : null}
        </React.Fragment>
    );
};
